import CallSplitIcon from '@mui/icons-material/CallSplit';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { wz } from '../../components/wizard-tokens';
import { FlowRow } from '../../campaign-wizard.types';
import { isEditableWait, rowIndentPx } from './flow-view.logic';

const MsgNumber: React.FC<{ n?: number }> = ({ n }) => (
  <div
    style={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      background: wz.primarySoft,
      color: wz.primary,
      fontSize: 12,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {n}
  </div>
);

const TitleBlock: React.FC<{ label: string; sub?: string }> = ({ label, sub }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
    <div style={{ fontSize: 14, fontWeight: 700 }}>{label}</div>
    {sub ? <div style={{ fontSize: 12, color: wz.muted }}>{sub}</div> : null}
  </div>
);

const Branch: React.FC<{ row: FlowRow; yes: boolean }> = ({ row, yes }) => (
  <>
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        background: yes ? wz.greenBg : wz.border,
        color: yes ? '#2B5A00' : wz.textDim,
        borderRadius: 10,
        padding: '2px 10px',
      }}
    >
      {row.label}
    </div>
    <div style={{ fontSize: 12, color: wz.muted }}>{row.sub}</div>
  </>
);

/** Angepasste Zeit (weicht von der Vorgabe ab): kleine Marke, damit man sie wiederfindet. */
const ChangedMark: React.FC<{ row: FlowRow }> = ({ row }) => {
  const { t } = useTranslation();
  return row.minutes !== undefined && row.defaultMinutes !== undefined && row.minutes !== row.defaultMinutes ? (
    <span style={{ fontSize: 11, fontWeight: 700, color: wz.primary, background: wz.primarySoft, borderRadius: 8, padding: '1px 7px' }}>
      {t('wizWaitChanged')}
    </span>
  ) : null;
};

const RowContent: React.FC<{ row: FlowRow }> = ({ row }) => {
  switch (row.kind) {
    case 'msg':
      return (
        <>
          <MsgNumber n={row.n} />
          <TitleBlock label={row.label} sub={row.sub} />
        </>
      );
    case 'wait':
      return (
        <>
          <HourglassEmptyOutlinedIcon
            sx={{ fontSize: 16, color: wz.dashed, width: 24, textAlign: 'center' }}
          />
          <div style={{ fontSize: 12, color: wz.muted }}>{row.label}</div>
          <ChangedMark row={row} />
        </>
      );
    case 'cond':
      return (
        <>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: wz.warnBg,
              color: wz.warnFg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CallSplitIcon sx={{ fontSize: 16 }} />
          </div>
          <TitleBlock label={row.label} sub={row.sub} />
          <ChangedMark row={row} />
        </>
      );
    case 'yes':
      return <Branch row={row} yes />;
    case 'no':
      return <Branch row={row} yes={false} />;
    default:
      return (
        <>
          <UndoIcon sx={{ fontSize: 18, color: wz.muted, width: 24, textAlign: 'center' }} />
          <div style={{ fontSize: 13, color: wz.text555 }}>{row.label}</div>
        </>
      );
  }
};

/** Ablaufliste mit Einrückung je Verzweigungstiefe (Design: `flowRows`). Wartezeiten sind anklickbar (`onEditWait`). */
const FlowPlanList: React.FC<{ rows: FlowRow[]; onEditWait?: (row: FlowRow) => void }> = ({
  rows,
  onEditWait,
}) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${wz.border}`,
        borderRadius: 10,
        padding: '10px 16px',
      }}
    >
      {rows.map((row, i) => {
        const editable = Boolean(onEditWait) && isEditableWait(row);
        return (
          <div
            key={`${i}-${row.kind}`}
            role={editable ? 'button' : undefined}
            tabIndex={editable ? 0 : undefined}
            title={editable ? t('wizWaitEditHint') : undefined}
            onClick={editable ? () => onEditWait?.(row) : undefined}
            onKeyDown={
              editable
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEditWait?.(row);
                    }
                  }
                : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '5px 0',
              paddingLeft: rowIndentPx(row.depth),
              cursor: editable ? 'pointer' : undefined,
            }}
          >
            <RowContent row={row} />
            {editable ? <EditOutlinedIcon sx={{ fontSize: 14, color: wz.muted }} /> : null}
          </div>
        );
      })}
    </div>
  );
};

export default FlowPlanList;
